import { Tooltip } from '@/components/ui/tooltip';
import { useArchiveAcknowledgmentStatusQuery } from '@/hooks/queries/use-archive-acknowledgment-status-query';
import { useArchiveMentionToDrive } from '@/hooks/actions/use-archive-mention-to-drive';
import { useDriveArchiveStateQuery } from '@/hooks/queries/use-drive-archive-state-query';
import { useDriveStatusQuery } from '@/hooks/queries/use-drive-status-query';
import { Box, Button, type ButtonProps, HStack, Text } from '@chakra-ui/react';
import { MentionDriveArchiveStatus } from '@fluenceur/types';
import type { MentionPlatform } from '@fluenceur/types';
import { GoogleDrive, GoogleDriveCheck, WarningTriangle, Clock } from 'iconoir-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveConfirmDialog } from './ArchiveConfirmDialog';

export interface SaveToDriveIconButtonProps extends Omit<ButtonProps, 'onClick' | 'aria-label'> {
  mentionId: string;
  platform: MentionPlatform;
  isExpired: boolean;
  isCreatorEnrolled: boolean;
}

const ICON_SIZE = 16;

/**
 * Save-to-Drive entry point on the mention preview top-bar.
 *
 * Render branches (in priority order):
 * 1. Story already past 24h: inline "Story expired" text, no button.
 * 2. Creator opted out: inline "Creator declined archiving" text, no button.
 * 3. Drive not connected: button labeled "Connect Drive to save",
 *    opens /settings/integrations in a new tab.
 * 4. Already archived: button opens the Drive file in a new tab.
 * 5. Default: icon-only button that opens the disclaimer modal.
 */
export const SaveToDriveIconButton: React.FC<SaveToDriveIconButtonProps> = ({
  mentionId,
  platform,
  isExpired,
  isCreatorEnrolled,
  ...buttonProps
}) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: driveStatus } = useDriveStatusQuery();
  const { data: archiveState } = useDriveArchiveStateQuery(mentionId);
  const { data: ackStatus } = useArchiveAcknowledgmentStatusQuery();
  const archiveMutation = useArchiveMentionToDrive();

  // 1. Expired media -> nothing to archive.
  if (isExpired) {
    return (
      <HStack
        gap={1.5}
        color="gray.500"
        fontSize="xs"
        py={1.5}
        px={2}
        bg="gray.50"
        rounded="md"
        aria-live="polite"
      >
        <Clock width={14} height={14} />
        <Text fontSize="xs">{t('mentions.archive.expired_inline')}</Text>
      </HStack>
    );
  }

  // 2. Creator opted out at the creator level (regardless of brand).
  if (archiveState?.status === MentionDriveArchiveStatus.OPTED_OUT) {
    return (
      <HStack
        gap={1.5}
        color="orange.700"
        fontSize="xs"
        py={1.5}
        px={2}
        bg="orange.50"
        borderWidth="1px"
        borderColor="orange.200"
        rounded="md"
        aria-live="polite"
      >
        <WarningTriangle width={14} height={14} />
        <Text fontSize="xs">{t('mentions.archive.opted_out_inline')}</Text>
      </HStack>
    );
  }

  // 3. Drive not connected: icon-only button + tooltip, like the other states.
  if (driveStatus && !driveStatus.isConnected) {
    return (
      <Tooltip content={t('mentions.archive.button.connect_drive')}>
        <Button
          variant="outline"
          size="sm"
          minW={8}
          boxSize={8}
          p={0}
          onClick={() => window.open('/settings/integrations', '_blank', 'noopener,noreferrer')}
          aria-label={t('mentions.archive.button.connect_drive')}
          {...buttonProps}
        >
          <GoogleDrive width={ICON_SIZE} height={ICON_SIZE} />
        </Button>
      </Tooltip>
    );
  }

  // 4. Already archived: iconoir's GoogleDriveCheck variant.
  if (
    archiveState?.status === MentionDriveArchiveStatus.ARCHIVED &&
    archiveState.driveFileWebViewLink
  ) {
    const link = archiveState.driveFileWebViewLink;
    return (
      <Tooltip content={t('mentions.archive.button.archived')}>
        <Button
          variant="outline"
          size="sm"
          minW={8}
          boxSize={8}
          p={0}
          onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
          aria-label={t('mentions.archive.button.archived')}
          colorPalette="green"
          {...buttonProps}
        >
          <GoogleDriveCheck width={ICON_SIZE} height={ICON_SIZE} />
        </Button>
      </Tooltip>
    );
  }

  // 5. Default. First click of the session: full ceremony modal.
  // Subsequent clicks (ack already recorded): direct enqueue, no modal.
  void platform;

  const handleClick = () => {
    if (ackStatus?.isAcknowledged) {
      archiveMutation.mutate({
        mentionId,
        disclaimerAcknowledged: true,
        nonAffiliateDisclaimerShown: !isCreatorEnrolled || undefined,
      });
      return;
    }
    setIsDialogOpen(true);
  };

  return (
    <>
      <Tooltip content={t('mentions.archive.button.label')}>
        <Button
          variant="outline"
          size="sm"
          minW={8}
          boxSize={8}
          p={0}
          loading={archiveMutation.isPending}
          onClick={handleClick}
          aria-label={t('mentions.archive.button.label')}
          {...buttonProps}
        >
          <Box>
            <GoogleDrive width={ICON_SIZE} height={ICON_SIZE} />
          </Box>
        </Button>
      </Tooltip>
      <ArchiveConfirmDialog
        isOpen={isDialogOpen}
        mentionId={mentionId}
        isCreatorEnrolled={isCreatorEnrolled}
        onClose={() => setIsDialogOpen(false)}
        onConfirmed={() => setIsDialogOpen(false)}
      />
    </>
  );
};
